import { __awaiter } from "tslib";
import * as messageFactory from "@org-avp/ave-message-factory";
import { MovementMode, } from "@org-avp/ave-message-factory";
import { log } from "../utils";
export class AveApiError extends Error {
    constructor(errorCode, errorMessage) {
        super(JSON.stringify({ errorCode, errorMessage }));
    }
}
export default class AveApi {
    constructor(timeoutTrackingService, datachannelHandler) {
        this.timeoutTrackingService = timeoutTrackingService;
        this.datachannelHandler = datachannelHandler;
    }
    selectCar(prCodes, consumptionData, startingAnimations, modelRevision) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendAveMessage(messageFactory.generateSelectCarRequest({
                prStringList: prCodes,
                animations: startingAnimations,
                consumptionData,
                modelRevision,
            }));
        });
    }
    changeCamera(cameraId_1) {
        return __awaiter(this, arguments, void 0, function* (cameraId, movementMode = MovementMode.flysmooth) {
            yield this.sendAveMessage(messageFactory.generateSelectCameraRequest({
                cameraId,
                transition: {
                    movementMode,
                },
            }));
        });
    }
    changeEnvironment(environmentId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendAveMessage(messageFactory.generateSelectEnvironmentRequest({ environmentName: environmentId }));
        });
    }
    getInteractionSpots() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = (yield this.datachannelHandler.sendMessage(messageFactory.generateGetInteractionSpotsRequest()));
            return response.data.interactionSpots;
        });
    }
    loadAnimations() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = (yield this.sendAveMessage(messageFactory.generateListAnimationsRequest()));
            return response.data.animations;
        });
    }
    loadCameras() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.sendAveMessage(messageFactory.generateListCamerasRequest());
            return response.data.cameras.map((aveCamera) => ({
                cameraId: aveCamera.id,
                tags: aveCamera.tags,
            }));
        });
    }
    loadEnvironments() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.sendAveMessage(messageFactory.generateListEnvironmentsRequest());
            return response.data.environments.map((aveEnvironment) => ({
                environmentId: aveEnvironment.name,
                label: aveEnvironment.label,
                tags: aveEnvironment.tags,
            }));
        });
    }
    triggerAnimation(animationId_1, animationDirection_1) {
        return __awaiter(this, arguments, void 0, function* (animationId, animationDirection, skipAnimationPlay = false) {
            return yield this.triggerAnimations([animationId], animationDirection, skipAnimationPlay);
        });
    }
    triggerAnimations(animationIds_1, direction_1) {
        return __awaiter(this, arguments, void 0, function* (animationIds, direction, skipAnimationPlay = false) {
            const response = yield this.sendAveMessage(messageFactory.generateTriggerAnimationsRequest({
                animationIds,
                direction,
                skip: skipAnimationPlay,
            }));
            return { animationDuration: response.data.animationLength };
        });
    }
    resetAllAnimations() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendAveMessage(messageFactory.generateEndAnimationsRequest());
        });
    }
    moveCamera(x_1, y_1, zoom_1) {
        return __awaiter(this, arguments, void 0, function* (x, y, zoom, panX = 0, panY = 0, focalLength = 0) {
            try {
                this.datachannelHandler.sendFireAndForgetMessage(messageFactory.generateCameraDeltaRequest({
                    yaw: x,
                    pitch: y,
                    zoom,
                    panX,
                    panY,
                    focalLength,
                }));
            }
            catch (error) {
                log.warn("Moving camera failed", error);
            }
            finally {
                this.timeoutTrackingService.resetIdleTimeout();
            }
        });
    }
    getAveState() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.sendAveMessage(messageFactory.generateGetStatesRequest());
            return response.data;
        });
    }
    renderImage(imageName, imageFormat, resolution, oversampling, transparentBackground) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = (yield this.sendAveMessage(messageFactory.generateRenderImageRequest({
                format: imageFormat,
                imageHeight: resolution.height,
                imageName,
                imageWidth: resolution.width,
                oversampling,
                transparentBackground,
            })));
            return response.data;
        });
    }
    setAnimationSpotsVisibility(isVisible) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendAveMessage(messageFactory.generateSetAnimationSpotsVisibilityRequest({ enabled: isVisible }));
        });
    }
    getAnimationSpots() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = (yield this.sendAveMessage(messageFactory.generateGetAnimationSpotsRequest()));
            return response.data.animationSpots;
        });
    }
    enableAnimationUpdate(animationUpdateHandler) {
        this.datachannelHandler.registerAnimationUpdateHandler(animationUpdateHandler);
    }
    disableAnimationUpdate() {
        this.datachannelHandler.deregisterAnimationUpdateHandler();
    }
    enableInteractionSpots(handler) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.receiveInteractionSpotsUpdate(true);
            this.datachannelHandler.registerInteractionSpotsHandler(handler);
        });
    }
    disableInteractionSpots() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.receiveInteractionSpotsUpdate(false);
            this.datachannelHandler.deregisterInteractionSpotsHandler();
        });
    }
    receiveInteractionSpotsUpdate(enabled) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendAveMessage(messageFactory.generateReceiveInteractionSpotsUpdateRequest({ enabled }));
        });
    }
    setCameraMode(cameraMode) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendAveMessage(messageFactory.generateSetCameraModeRequest({ cameraMode }));
        });
    }
    setSteeringAngle(degrees) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendAveMessage(messageFactory.generateSetSteeringAngleRequest({ degrees }));
        });
    }
    setCameraDefinition(cameraDefinition) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendAveMessage(messageFactory.generateSetCameraDefinitionRequest({ cameraDefinition }));
        });
    }
    getCurrentCameraDefinition() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = (yield this.sendAveMessage(messageFactory.generateGetCurrentCameraDefinitionRequest()));
            return response.data;
        });
    }
    setCarTransform(location, rotation) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendAveMessage(messageFactory.generateSetCarTransformRequest({ location, rotation }));
        });
    }
    resetCamera() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendAveMessage(messageFactory.generateResetCameraRequest());
        });
    }
    startOrResumeCameraOrbit(orbit) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendAveMessage(messageFactory.generateStartOrResumeCameraOrbitRequest({ orbit }));
        });
    }
    modifyCameraOrbit(command) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendAveMessage(messageFactory.generateModifyCameraOrbitRequest({ command: command }));
        });
    }
    stopCameraOrbit() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendAveMessage(messageFactory.generateStopCameraOrbitRequest());
        });
    }
    listCameraOrbits() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = (yield this.sendAveMessage(messageFactory.generateListCameraOrbitsRequest()));
            return response.data.cameraOrbits;
        });
    }
    listModelConfigurations() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = (yield this.sendAveMessage(messageFactory.generateListModelConfigurationsRequest()));
            return response.data.vehicleMetaData;
        });
    }
    listDrivingScenes() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = (yield this.sendAveMessage(messageFactory.generateListDrivingScenesRequest()));
            return response.data.drivingScenes;
        });
    }
    startDrivingScene(name, drivingSceneFinishedHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendAveMessage(messageFactory.generateDrivingSceneStartRequest({ name }));
            this.datachannelHandler.registerDrivingSceneFinishedHandler(drivingSceneFinishedHandler);
        });
    }
    stopDrivingScene() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendAveMessage(messageFactory.generateDrivingSceneStopRequest());
        });
    }
    displayConsumptionData(enabled) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendAveMessage(messageFactory.generateDisplayConsumptionDataRequest({ enabled }));
        });
    }
    startSession(_a) {
        return __awaiter(this, arguments, void 0, function* ({ sessionId, client, messageId, consumptionDataState, vehicle, environment, camera, loadingScreen, }) {
            return (yield this.sendAveMessage(messageFactory.generateStartSessionRequest({
                sessionId,
                client,
                messageId,
                consumptionDataState,
                vehicle,
                environment,
                camera,
                loadingScreen,
            })));
        });
    }
    endSession() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.sendAveMessage(messageFactory.generateEndSessionRequest()));
        });
    }
    sendAveMessage(aveRequest_1) {
        return __awaiter(this, arguments, void 0, function* (aveRequest, skipTimeout = false) {
            try {
                const response = yield this.datachannelHandler.sendMessage(aveRequest, skipTimeout);
                if (response instanceof Error) {
                    throw response;
                }
                return response;
            }
            finally {
                this.timeoutTrackingService.resetIdleTimeout();
            }
        });
    }
}
//# sourceMappingURL=ave-api.js.map